import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import NewsSidebar from './components/NewsSidebar';
import { articles, categories } from '@/mocks/guagua';
import { formatViews, getCategoryName } from '@/utils/content';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const PAGE_SIZE = 6;

export default function NewsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') ?? 'all';
  const query = searchParams.get('q') ?? '';

  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [keyword, setKeyword] = useState(query);

  useDocumentMeta(
    '新瓜速递 - 最新更新快讯列表｜瓜哥吃瓜',
    '瓜哥吃瓜新瓜速递栏目，实时汇总娱乐圈、影视综艺、网络热议等六大栏目动态，支持按栏目筛选与关键词搜索，最新更新快讯不间断。',
  );

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [activeCat, query, sort]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = articles.filter((item) => {
      const matchCat = activeCat === 'all' || item.categorySlug === activeCat;
      if (!matchCat) return false;
      if (!q) return true;
      const haystack = `${item.title}${item.excerpt}${item.tags.join('')}${getCategoryName(item.categorySlug)}`.toLowerCase();
      return haystack.includes(q);
    });
    if (sort === 'hot') {
      return [...list].sort((a, b) => b.views - a.views);
    }
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeCat, query, sort]);

  const currentCategory = categories.find((item) => item.slug === activeCat);
  const shown = filtered.slice(0, visible);

  const updateParams = (nextCat: string, nextQuery: string) => {
    const params: Record<string, string> = {};
    if (nextCat && nextCat !== 'all') params.cat = nextCat;
    if (nextQuery.trim()) params.q = nextQuery.trim();
    setSearchParams(params);
  };

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    updateParams(activeCat, keyword);
  };

  const resetFilter = () => {
    setKeyword('');
    setSearchParams({});
  };

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '新瓜速递',
      description: '瓜哥吃瓜新瓜速递栏目，实时汇总热门动态与最新更新快讯。',
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dfhov.cn/' },
        { '@type': 'ListItem', position: 2, name: '新瓜速递', item: 'https://dfhov.cn/news' },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd data={schema} />
      <SiteHeader />
      <main className="flex-1 pt-16 md:pt-20">
        <div className="px-4 md:px-6 py-8 md:py-10 bg-background-100 border-b border-background-200">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '新瓜速递' }]} />
            <h1 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-foreground-950">
              {currentCategory ? currentCategory.name : '新瓜速递'}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              {currentCategory
                ? currentCategory.desc
                : '刚刚更新的一批内容都在这里。可以按栏目筛，也可以直接搜关键词，找到你关心的那一口瓜。'}
            </p>
          </div>
        </div>

        <div className="px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-6">
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => updateParams('all', query)}
                  className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    activeCat === 'all'
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  全部
                </button>
                {categories.map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => updateParams(item.slug, query)}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      activeCat === item.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <form onSubmit={handleSearch} className="flex-1 lg:flex-none" role="search">
                  <div className="relative">
                    <i
                      className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
                      aria-hidden="true"
                    ></i>
                    <input
                      type="search"
                      value={keyword}
                      onChange={(event) => setKeyword(event.target.value)}
                      placeholder="搜索标题、标签"
                      aria-label="搜索内容"
                      className="w-full lg:w-56 pl-9 pr-3 py-2 rounded-md text-sm bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                    />
                  </div>
                </form>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
                  <button
                    type="button"
                    onClick={() => setSort('latest')}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      sort === 'latest' ? 'bg-secondary-500 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    最新
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort('hot')}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      sort === 'hot' ? 'bg-secondary-500 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    最热
                  </button>
                </div>
              </div>
            </div>

            {(query || activeCat !== 'all') && (
              <div className="flex flex-wrap items-center gap-2 mb-6 text-sm text-foreground-600">
                <span>
                  共找到 <strong className="text-primary-600">{filtered.length}</strong> 条结果
                </span>
                {query && (
                  <span className="px-2 py-0.5 rounded bg-background-100 text-foreground-600">
                    关键词：{query}
                  </span>
                )}
                {currentCategory && (
                  <span className="px-2 py-0.5 rounded bg-background-100 text-foreground-600">
                    栏目：{currentCategory.name}
                  </span>
                )}
                <button
                  type="button"
                  onClick={resetFilter}
                  className="flex items-center gap-1 text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  <i className="ri-close-circle-line" aria-hidden="true"></i>
                  清除筛选
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
              <div className="lg:col-span-8">
                {shown.length > 0 ? (
                  <>
                    <div className="rounded-lg bg-background-50 border border-background-200 divide-y divide-background-200 px-4 md:px-6">
                      {shown.map((item) => (
                        <article key={item.id} className="py-5">
                          <Link
                            to={`/news/${item.id}`}
                            className="group grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 cursor-pointer"
                          >
                            <div className="relative w-full h-44 sm:h-32 rounded-md overflow-hidden bg-background-200">
                              <img
                                src={item.image}
                                alt={item.title}
                                title={`${item.title} - 瓜哥吃瓜`}
                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                            <div className="min-w-0 flex flex-col">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700">
                                  {getCategoryName(item.categorySlug)}
                                </span>
                                <span className="text-xs text-foreground-400">{item.date}</span>
                              </div>
                              <h2 className="font-heading text-lg font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                                {item.title}
                              </h2>
                              <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
                                {item.excerpt}
                              </p>
                              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
                                <span className="flex items-center gap-1">
                                  <i className="ri-user-3-line" aria-hidden="true"></i>
                                  {item.author}
                                </span>
                                <span className="flex items-center gap-1">
                                  <i className="ri-eye-line" aria-hidden="true"></i>
                                  {formatViews(item.views)} 阅读
                                </span>
                                <span className="flex items-center gap-1">
                                  <i className="ri-chat-3-line" aria-hidden="true"></i>
                                  {item.comments} 条讨论
                                </span>
                              </div>
                            </div>
                          </Link>
                        </article>
                      ))}
                    </div>

                    {visible < filtered.length ? (
                      <button
                        type="button"
                        onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                        className="mt-6 w-full py-3 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        加载更多（剩余 {filtered.length - visible} 条）
                      </button>
                    ) : (
                      <p className="mt-6 text-center text-xs text-foreground-400">
                        已经到底啦，去首页看看快讯时间线吧
                      </p>
                    )}
                  </>
                ) : (
                  <div className="rounded-lg bg-background-50 border border-background-200 py-16 px-6 text-center">
                    <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-background-100 text-foreground-400 text-2xl">
                      <i className="ri-search-eye-line" aria-hidden="true"></i>
                    </span>
                    <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                      没有找到相关内容
                    </h2>
                    <p className="mt-2 text-sm text-foreground-500">
                      换个关键词，或者回到全部栏目看看别的新瓜。
                    </p>
                    <button
                      type="button"
                      onClick={resetFilter}
                      className="mt-5 inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      查看全部内容
                    </button>
                  </div>
                )}
              </div>

              <aside className="lg:col-span-4">
                <NewsSidebar />
              </aside>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}